import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import {
    Badge,
    Box,
    Button,
    Card,
    Center,
    Divider,
    Group,
    Loader,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
    Title,
} from '@mantine/core'
import {
    IconArrowRight,
    IconBroadcast,
    IconPlus,
    IconRefresh,
} from '@tabler/icons-react'
import { formatDistanceToNow } from 'date-fns'
import { formatCompactNumber } from '@/lib/formatNumber'
import type { Destination, DestinationType } from '@/features/segment/types'
import { useDistributionsInventory } from '@/hooks/useDistributionsInventory'
import { useSegmentStubs } from '@/features/segment/queries'
import {
    destinationConnectionReady,
    destinationType,
    DESTINATION_TYPE_LABEL,
} from '@/lib/marketplaceDestinations'
import { matchColor } from '@/lib/matchColor'
import { DestinationConnectionModal } from '@/features/segment/components/DestinationConnectionModal'
import { AddConnectionModal } from '@/features/segment/components/AddConnectionModal'
import { DeleteButton } from '@/components/DeleteButton'
import { EmptyState } from '@/components/EmptyState'
import { AttentionBanner } from '@/components/AttentionBanner'

// Connection health is derived from the lazy config (see destinationConnectionReady),
// not a stored status: a destination is connected once its connection is set up.
type DestinationHealth = 'connected' | 'needs-setup'

const HEALTH_META: Record<
    DestinationHealth,
    { label: string; color: string; rank: number }
> = {
    'needs-setup': { label: 'Needs setup', color: 'warning', rank: 0 },
    connected: { label: 'Connected', color: 'success', rank: 1 },
}

// What "success" means per destination type (navigation-ia.md §5).
const TYPE_SUCCESS: Record<DestinationType, string> = {
    dsp: 'Match / addressability rate',
    identity: 'Resolution / onboarding rate',
    'data-delivery': 'Rows delivered — no match step',
}

const STALE_DAYS = 7

interface DestRollup {
    runs: number
    audiences: number
    segments: number
    lastPushAt?: string
    volume: number
    avgMatchPct: number | null
}

export function DestinationsPage() {
    const { rows, destinations, isLoading } = useDistributionsInventory()
    const { savedConfig, removeDestination } = useSegmentStubs()
    const [addOpen, setAddOpen] = useState(false)
    // The destination whose connection is being set up, if any.
    const [connecting, setConnecting] = useState<Destination | null>(null)
    const now = Date.now()

    const healthOf = (dest: Destination): DestinationHealth =>
        destinationConnectionReady(dest.platform, savedConfig)
            ? 'connected'
            : 'needs-setup'

    // Per-destination "what's flowing", derived from the distribution runs.
    const rollups = useMemo(() => {
        const map = new Map<string, DestRollup>()
        for (const dest of destinations) {
            const mine = rows.filter(
                (r) => r.distribution.destinationId === dest.id
            )
            const audiences = new Set(mine.map((r) => r.threadId))
            const segments = new Set(mine.map((r) => r.distribution.segmentKey))
            const ran = mine.filter((r) => r.latestRun?.runAt)
            const lastPushAt = ran.length
                ? ran
                      .map((r) => r.latestRun?.runAt as string)
                      .sort()
                      .at(-1)
                : undefined
            const landed = mine.filter(
                (r) =>
                    r.latestRun?.status === 'succeeded' ||
                    r.latestRun?.status === 'partial'
            )
            const volume = landed.reduce(
                (sum, r) => sum + (r.latestRun?.recordsPushed ?? 0),
                0
            )
            const matchable = landed.filter(
                (r) =>
                    r.latestRun?.recordsMatched != null &&
                    r.latestRun?.recordsPushed
            )
            const avgMatchPct = matchable.length
                ? Math.round(
                      (matchable.reduce(
                          (sum, r) =>
                              sum +
                              (r.latestRun?.recordsMatched as number) /
                                  (r.latestRun?.recordsPushed as number),
                          0
                      ) /
                          matchable.length) *
                          100
                  )
                : null
            map.set(dest.id, {
                runs: mine.length,
                audiences: audiences.size,
                segments: segments.size,
                lastPushAt,
                volume,
                avgMatchPct,
            })
        }
        return map
    }, [rows, destinations])

    const sorted = useMemo(
        () =>
            [...destinations].sort(
                (a, b) =>
                    HEALTH_META[healthOf(a)].rank -
                    HEALTH_META[healthOf(b)].rank
            ),
        // healthOf depends on savedConfig; re-sort when either changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [destinations, savedConfig]
    )

    const unhealthy = destinations.filter(
        (d) => healthOf(d) === 'needs-setup'
    ).length

    return (
        <Stack gap='lg' h='100%'>
            <Group
                justify='space-between'
                align='flex-start'
                style={{ flexShrink: 0 }}
            >
                <div>
                    <Title order={2}>Destinations</Title>
                    <Text c='dimmed'>
                        The platforms you can send to — their health, and what
                        is flowing through them.
                    </Text>
                </div>
                <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={() => setAddOpen(true)}
                >
                    Add connection
                </Button>
            </Group>

            {isLoading ? (
                <Center style={{ flex: 1 }}>
                    <Loader />
                </Center>
            ) : destinations.length === 0 ? (
                <EmptyState
                    icon={<IconBroadcast size={28} />}
                    title='No destinations connected'
                    description='Connect a platform to start sending audiences to it.'
                />
            ) : (
                <>
                    <Stack gap='lg' style={{ flexShrink: 0 }}>
                        {unhealthy > 0 && (
                            <AttentionBanner
                                title={
                                    unhealthy === 1
                                        ? '1 connection needs attention'
                                        : `${unhealthy} connections need attention`
                                }
                            >
                                A connection that needs reauthorization or has
                                an error silently blocks delivery — reconnect it
                                to resume.
                            </AttentionBanner>
                        )}
                    </Stack>

                    <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                            {sorted.map((dest) => (
                                <DestinationCard
                                    key={dest.id}
                                    dest={dest}
                                    health={healthOf(dest)}
                                    rollup={rollups.get(dest.id)}
                                    now={now}
                                    onReconnect={() => setConnecting(dest)}
                                    onDelete={() => removeDestination(dest.id)}
                                    viewDistributionsTo={`/distributions?dest=${dest.id}`}
                                />
                            ))}
                        </SimpleGrid>
                    </Box>
                </>
            )}

            <AddConnectionModal
                existingPlatforms={destinations.map((d) => d.platform)}
                opened={addOpen}
                onClose={() => setAddOpen(false)}
                onAdded={(dest) => setConnecting(dest)}
            />

            {connecting && (
                <DestinationConnectionModal
                    destination={connecting}
                    opened
                    onClose={() => setConnecting(null)}
                />
            )}
        </Stack>
    )
}

function DestinationCard({
    dest,
    health,
    rollup,
    now,
    onReconnect,
    onDelete,
    viewDistributionsTo,
}: {
    dest: Destination
    health: DestinationHealth
    rollup?: DestRollup
    now: number
    onReconnect: () => void
    onDelete: () => void
    viewDistributionsTo: string
}) {
    const healthMeta = HEALTH_META[health]
    const needsAttention = health === 'needs-setup'
    const type = destinationType(dest.platform)
    const r = rollup
    const neverUsed = !r || r.runs === 0
    const stale =
        r?.lastPushAt != null &&
        now - Date.parse(r.lastPushAt) > STALE_DAYS * 24 * 3600 * 1000

    return (
        <Card
            withBorder
            radius='md'
            padding='lg'
            style={
                needsAttention
                    ? {
                          borderColor: `var(--mantine-color-${healthMeta.color}-5)`,
                      }
                    : undefined
            }
        >
            <Stack gap='sm' h='100%'>
                <Group justify='space-between' wrap='nowrap' align='flex-start'>
                    <Group gap='xs' wrap='nowrap'>
                        <ThemeIcon variant='light' radius='md'>
                            <IconBroadcast size={18} />
                        </ThemeIcon>
                        <Text fw={600}>{dest.platform}</Text>
                    </Group>
                    <Badge variant='light' color={healthMeta.color}>
                        {healthMeta.label}
                    </Badge>
                </Group>

                <div>
                    <Badge variant='light' color='gray' size='sm'>
                        {DESTINATION_TYPE_LABEL[type]}
                    </Badge>
                    <Text size='xs' c='dimmed' mt={4}>
                        Success = {TYPE_SUCCESS[type].toLowerCase()}
                    </Text>
                </div>

                <Divider />

                {neverUsed ? (
                    <Text size='sm' c='dimmed'>
                        Never used
                    </Text>
                ) : (
                    <Stack gap={2}>
                        <Text size='sm'>
                            {r.audiences} audience{r.audiences === 1 ? '' : 's'}{' '}
                            · {r.segments} segment{r.segments === 1 ? '' : 's'}
                        </Text>
                        <Text size='xs' c='dimmed'>
                            {r.lastPushAt
                                ? `last push ${formatDistanceToNow(new Date(r.lastPushAt), { addSuffix: true })}`
                                : 'no completed push yet'}
                            {stale && ' · stale'}
                        </Text>
                        {type === 'data-delivery' ? (
                            <Text size='xs' c='dimmed'>
                                Delivered {formatCompactNumber(r.volume)} rows
                            </Text>
                        ) : r.avgMatchPct != null ? (
                            <Text size='xs'>
                                <Text
                                    span
                                    c={matchColor(r.avgMatchPct)}
                                    fw={600}
                                >
                                    {r.avgMatchPct}% avg match
                                </Text>{' '}
                                · {formatCompactNumber(r.volume)} pushed
                            </Text>
                        ) : (
                            <Text size='xs' c='dimmed'>
                                nothing delivered yet
                            </Text>
                        )}
                    </Stack>
                )}

                {needsAttention && (
                    <Text size='xs' c={healthMeta.color}>
                        Finish connection setup to deliver
                    </Text>
                )}

                <Group gap='xs' mt='auto' pt='xs' justify='space-between'>
                    <Group gap='xs'>
                        {needsAttention && (
                            <Button
                                size='xs'
                                variant='light'
                                color={healthMeta.color}
                                leftSection={<IconRefresh size={14} />}
                                onClick={onReconnect}
                            >
                                Finish setup
                            </Button>
                        )}
                        <Button
                            size='xs'
                            variant='light'
                            component={Link}
                            to={viewDistributionsTo}
                            rightSection={<IconArrowRight size={14} />}
                        >
                            View distributions
                        </Button>
                    </Group>
                    <DeleteButton
                        onDelete={onDelete}
                        title='Delete destination'
                        confirmTitle={`Delete ${dest.platform}?`}
                        confirmMessage='Removes this destination and its connection. Distributions sent here keep their run history but stop delivering until reconnected.'
                    />
                </Group>
            </Stack>
        </Card>
    )
}
